import React, { useEffect, useState } from 'react';
import api from '@/api';
import { useNavigate, useLocation } from 'react-router-dom';
import { Clock, Warning, X, ArrowRight } from '@phosphor-icons/react';

const TrialBanner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem('trial_banner_dismissed') === '1');

  useEffect(() => {
    const fetchIt = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const r = await api.get('/billing/status');
        setStatus(r.data);
      } catch { /* silent */ }
    };
    fetchIt();
  }, [location.pathname]);

  if (!status) return null;
  if (status.contract?.enabled) return <ContractBanner contract={status.contract} pathname={location.pathname} onOpen={() => navigate('/vc/billing')} />;
  if (status.card) return null;  // card on file → no banner
  const isFund = status.kind === 'fund';

  const showAt30Pct = status.in_trial && (status.trial_days_left / status.trial_total_days) <= 0.3;
  const expired = !status.in_trial;
  if (!showAt30Pct && !expired) return null;

  // Expired trials are critical (add a card to keep the team active), so the
  // banner stays put. Dismissal only silences the pre-expiry "X days left" warning.
  if (dismissed && !expired) return null;

  // Hide on the billing page itself
  if (/\/billing/.test(location.pathname)) return null;

  const isUrgent = expired || status.trial_days_left <= 1;
  const tone = isUrgent ? 'bg-danger text-white' : 'bg-warning text-black';

  return (
    <div className={`${tone} px-4 py-2.5 flex items-center justify-center gap-3 text-sm font-medium relative z-40`} data-testid="trial-banner">
      {isUrgent ? <Warning size={16} weight="fill" /> : <Clock size={16} weight="fill" />}
      <span className="hidden md:inline">
        {expired
          ? (isFund ? 'Your free trial has ended. Contact support for billing details.' : 'Your free trial has expired. Add a card to keep your team active.')
          : status.trial_days_left_int >= 1
            ? `${status.trial_days_left_int} day${status.trial_days_left_int === 1 ? '' : 's'} left in your free trial.`
            : `${status.trial_hours_left} hour${status.trial_hours_left === 1 ? '' : 's'} left in your free trial.`}
      </span>
      <span className="md:hidden">
        {expired ? 'Trial expired' : `${status.trial_days_left_int || `${status.trial_hours_left}h`} left`}
      </span>
      <button onClick={() => navigate(status.kind === 'fund' ? '/vc/billing' : '/startup/billing')}
        data-testid="trial-banner-cta"
        className="ml-2 inline-flex items-center gap-1 px-3 py-1 rounded bg-black/15 hover:bg-black/25 text-xs font-bold uppercase tracking-wider transition-colors">
        {isFund ? 'Billing details' : 'Add card'} <ArrowRight size={11} weight="bold" />
      </button>
      {!expired && (
        <button onClick={() => { sessionStorage.setItem('trial_banner_dismissed', '1'); setDismissed(true); }}
          className="ml-1 p-1 rounded hover:bg-black/15" data-testid="trial-banner-dismiss" aria-label="Dismiss">
          <X size={13} weight="bold" />
        </button>
      )}
    </div>
  );
};

// Incubator/VC negotiated contract (vc_billing.py): renewal due → expired → paused.
const fmtDay = (iso) => new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

const ContractBanner = ({ contract: c, pathname, onOpen }) => {
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem('contract_banner_dismissed') === '1');
  let message;
  let critical = false;
  if (c.state === 'paused') {
    message = c.pause_reason === 'unpaid'
      ? 'Your subscription is paused — payment is overdue. Pay on the Billing page to restore access.'
      : 'Your subscription is paused. Contact support to renew and restore access.';
    critical = true;
  } else if (c.state === 'grace') {
    message = `Your contract expired on ${fmtDay(c.expiry_date)}. Your subscription will be paused on ${fmtDay(c.pause_date)}.`;
    critical = true;
  } else if (c.state === 'payment_due') {
    message = c.payment?.pause_date
      ? `Your Founderboard subscription payment is due. Pay by ${fmtDay(c.payment.pause_date)} to avoid your workspace being paused.`
      : 'Your Founderboard subscription payment is due.';
    critical = true;
  } else if (c.due_soon && c.state !== 'anchor') {
    message = c.days_to_expiry <= 0
      ? 'Your Founderboard subscription is due for renewal today.'
      : `Your Founderboard subscription is due for renewal in ${c.days_to_expiry} day${c.days_to_expiry === 1 ? '' : 's'}.`;
  }
  if (!message) return null;
  // Pre-expiry reminders can be silenced for the session; expiry/pause can't.
  if (dismissed && !critical) return null;
  if (/\/billing/.test(pathname)) return null;

  return (
    <div className={`${critical ? 'bg-danger text-white' : 'bg-warning text-black'} px-4 py-2.5 flex items-center justify-center gap-3 text-sm font-medium relative z-40`} data-testid="contract-banner" data-state={c.state}>
      {critical ? <Warning size={16} weight="fill" /> : <Clock size={16} weight="fill" />}
      <span>{message}</span>
      <button onClick={onOpen} data-testid="contract-banner-cta"
        className="ml-2 inline-flex items-center gap-1 px-3 py-1 rounded bg-black/15 hover:bg-black/25 text-xs font-bold uppercase tracking-wider transition-colors">
        Billing <ArrowRight size={11} weight="bold" />
      </button>
      {!critical && (
        <button onClick={() => { sessionStorage.setItem('contract_banner_dismissed', '1'); setDismissed(true); }}
          className="ml-1 p-1 rounded hover:bg-black/15" data-testid="contract-banner-dismiss" aria-label="Dismiss">
          <X size={13} weight="bold" />
        </button>
      )}
    </div>
  );
};

export default TrialBanner;
